import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import PdfCanvasViewer from '../components/PdfCanvasViewer';

/* Standalone document viewer — the page FSC's "View Document" button opens.
 *
 * Before this, that button opened `/api/downloads/{token}` directly, so Chrome
 * rendered the PDF in its built-in viewer and offered "Summarize with Gemini"
 * on every client document. Now FSC opens `/view/{token}`, this page redeems
 * the token itself, and the bytes are rendered to a canvas by PdfCanvasViewer.
 * Chrome never receives a PDF document, so its viewer never engages.
 *
 * NO AUTH DEPENDENCY, deliberately, and consistent with the route it replaces:
 * authorization happened when the token was minted (the Apex behind the FSC
 * button is `with sharing` and re-queries the record), the token is single-use
 * and 5-minute, and the caller is an FSC user who has no hub session. Adding a
 * hub login here would break every link for advisors who legitimately see the
 * document in the CRM.
 *
 * THE TOKEN IS SINGLE-USE, which shapes everything below:
 *   - this page is served without touching the token; only the fetch below
 *     redeems it, so a page load never burns the link on its own;
 *   - the fetch is ref-guarded, because React 18 StrictMode double-invokes
 *     effects in development and the second call would 410;
 *   - the bytes are kept in memory and every later action — render, zoom,
 *     download — is served from that copy. There is no second request to make.
 */
export default function DocumentView() {
  const { token } = useParams();
  const claimed = useRef(false);

  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    (async () => {
      try {
        const resp = await fetch(`/api/downloads/${encodeURIComponent(token)}`);
        if (resp.status === 410) {
          setState({ status: 'error', message: 'This link has already been used or has expired. Open the document again from Salesforce.' });
          return;
        }
        if (!resp.ok) {
          setState({ status: 'error', message: `Could not retrieve the file (HTTP ${resp.status}).` });
          return;
        }
        const type = (resp.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
        const name = filenameFrom(resp.headers.get('Content-Disposition'));
        const buf = await resp.arrayBuffer();
        setState({ status: 'ready', type, filename: name, data: buf });
      } catch {
        setState({ status: 'error', message: 'Could not retrieve the file.' });
      }
    })();
  }, [token]);

  // Everything downloadable comes off the in-memory copy — the server link is
  // already spent, so a second navigation to it would 410.
  const download = useCallback(() => {
    if (state.status !== 'ready') return;
    const blob = new Blob([state.data], { type: state.type || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = state.filename || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, [state]);

  document.title = state.filename ? `${state.filename} — Prosperity Hub` : 'Document — Prosperity Hub';

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '.6rem', flexShrink: 0,
        padding: '.55rem .9rem', borderBottom: '1px solid #dde2e8', background: '#0a2947', color: '#fff',
      }}>
        <i className="bi bi-file-earmark-text" />
        <span style={{
          fontWeight: 600, fontSize: '.9rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {state.filename || 'Document'}
        </span>
      </div>
      <Body state={state} onDownload={download} />
    </div>
  );
}

/* The fetch's outcome, in one object: 'loading' carries nothing, 'error' carries
 * a message, 'ready' carries the bytes and what they are. Declared once because
 * <Body> is the only consumer and it switches on every field. */
const VIEWER_STATE = PropTypes.shape({
  status: PropTypes.oneOf(['loading', 'error', 'ready']).isRequired,
  message: PropTypes.string,
  type: PropTypes.string,
  filename: PropTypes.string,
  data: PropTypes.instanceOf(ArrayBuffer),
});

function Body({ state, onDownload }) {
  if (state.status === 'loading') {
    return <Centered><span className="spinner-border spinner-border-sm me-2" />Opening document…</Centered>;
  }
  if (state.status === 'error') {
    return <Centered tone="#b42318">{state.message}</Centered>;
  }

  const { type, filename, data } = state;

  if (type === 'application/pdf') {
    return <PdfCanvasViewer data={data} filename={filename} onDownload={onDownload} />;
  }

  if (type.startsWith('image/')) {
    return <ImageBody data={data} type={type} filename={filename} onDownload={onDownload} />;
  }

  // Salentica `.snote` notes are stored as raw HTML, including inline base64
  // images. They are migration content, not our markup, so they render inside a
  // sandboxed iframe with no allow-scripts and no allow-same-origin — the same
  // posture the Compliance Quick Hitter viewer uses. srcdoc keeps it in-document
  // rather than minting a blob: URL the browser could treat as a navigation.
  if (type === 'text/html') {
    return <HtmlBody data={data} filename={filename} onDownload={onDownload} />;
  }

  // Everything else — xlsx/xlsm/docx/zip/msg, ~2% of the corpus. These never
  // rendered in a browser tab anyway (Chrome downloads them), so falling back to
  // a download is today's behaviour, not a regression — and no browser PDF
  // viewer is involved, so there is nothing to shield.
  return (
    <Centered>
      <div className="text-center">
        <i className="bi bi-file-earmark-arrow-down" style={{ fontSize: '2rem', color: '#6b7280' }} />
        <div className="mt-2 mb-1" style={{ fontWeight: 600 }}>{filename || 'Document'}</div>
        <div className="text-muted mb-3" style={{ fontSize: '.85rem' }}>
          This file type can&apos;t be previewed in the browser.
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={onDownload}>
          <i className="bi bi-download me-1" /> Download
        </button>
      </div>
    </Centered>
  );
}

Body.propTypes = { state: VIEWER_STATE.isRequired, onDownload: PropTypes.func.isRequired };

function ImageBody({ data, type, filename, onDownload }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const u = URL.createObjectURL(new Blob([data], { type }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [data, type]);
  return (
    <div style={{ flex: 1, overflow: 'auto', background: '#525659', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      {url && <img src={url} alt={filename || 'Document'} onDoubleClick={onDownload}
                   style={{ maxWidth: '100%', maxHeight: '100%', boxShadow: '0 2px 10px rgba(0,0,0,.35)' }} />}
    </div>
  );
}

ImageBody.propTypes = {
  data: PropTypes.instanceOf(ArrayBuffer).isRequired,
  type: PropTypes.string.isRequired,
  filename: PropTypes.string,
  onDownload: PropTypes.func,
};

function HtmlBody({ data, filename, onDownload }) {
  const [html, setHtml] = useState('');
  useEffect(() => { setHtml(new TextDecoder('utf-8').decode(data)); }, [data]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ padding: '.4rem .75rem', borderBottom: '1px solid #dde2e8',
                    background: '#f7f9fb', flexShrink: 0, textAlign: 'right' }}>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onDownload}>
          <i className="bi bi-download me-1" /> Download
        </button>
      </div>
      <iframe
        title={filename || 'Note'}
        srcDoc={html}
        sandbox=""
        style={{ flex: 1, border: 0, width: '100%', background: '#fff' }}
      />
    </div>
  );
}

HtmlBody.propTypes = {
  data: PropTypes.instanceOf(ArrayBuffer).isRequired,
  filename: PropTypes.string,
  onDownload: PropTypes.func,
};

function Centered({ children, tone }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: tone || '#4b5563', fontSize: '.9rem', padding: '2rem' }}>
      {children}
    </div>
  );
}

Centered.propTypes = { children: PropTypes.node, tone: PropTypes.string };

/* RFC 6266: prefer filename*=UTF-8''… when present, else the plain filename. */
function filenameFrom(header) {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) { try { return decodeURIComponent(star[1]); } catch { /* fall through */ } }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}
